import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BugReportIcon from '@mui/icons-material/BugReport';
import ComputerIcon from '@mui/icons-material/Computer';
import GetAppIcon from '@mui/icons-material/GetApp';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { getSecurityEndpointDetailsHref, getSecurityNavItemPath, getSecurityVulnerabilityDetailHref, SECURITY_ENDPOINTS_HREF } from 'src/mindtime/config/securityNav';
import {
  SECURITY_PAGE_FILTER_KEYS,
  SECURITY_VULNERABILITY_FILTER_DEFAULTS,
} from 'src/mindtime/constants/securityFilters';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrGlobalScopeFilters,
  EdrKpiCard,
  EdrPageHeader,
  EdrSeverityPill,
  EdrStatusPill,
  edrMonoStyle,
  edrMutedStyle,
  formatEndpointLabel,
  edrEndpointNameLinkStyle,
  SubscriptionUnavailableMessage,
  type EdrToolbarFilter,
  formatEdrNumber,
  EDR_SEVERITY_FILTER_OPTIONS,
} from '../shared/edrPrimitives';
import type { Column, TableData } from 'src/types/table';
import type {
  WazuhVulnerableEndpointItem,
  WazuhVulnerabilityItem,
} from 'src/types/mindtime/wazuhVulnerabilities';
import { useVulnerabilitiesData, useVulnerabilityExport, type VulnerabilitySortParams } from './useVulnerabilitiesData';
import {
  getVulnerabilityId,
  toFilterListLabel,
  toVulnerabilityTitle,
  vulnerabilitySeverityToLevel,
  vulnerabilityStatusToTone,
} from './vulnerabilityDetailModel';


const getCvssColor = (score: number) => {
  if (score >= 9) return EDR_COLORS.critical;
  if (score >= 7) return EDR_COLORS.high;
  if (score >= 4) return EDR_COLORS.medium;
  return EDR_COLORS.success;
};

const CvssCell: FC<{ score: number }> = ({ score }) => {
  if (typeof score !== 'number' || Number.isNaN(score) || score < 0) {
    return <span style={edrMutedStyle}>N/A</span>;
  }

  const color = getCvssColor(score);
  const pct = Math.min(100, (score / 10) * 100);
  return (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
      <span style={{ ...edrMonoStyle, fontWeight: 700, color, minWidth: 28 }}>
        {score.toFixed(1)}
      </span>
      <Box
        style={{
          flex: 1,
          height: 4,
          borderRadius: 2,
          background: EDR_COLORS.slate200,
          minWidth: 48,
          maxWidth: 72,
        }}
      >
        <Box style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: color }} />
      </Box>
    </Box>
  );
};

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Vulnerability Detection', to: getSecurityNavItemPath('vulnerability-detection') ?? '' },
  ],
};


const STATUS_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
];

const CVSS_OPTIONS = [
  { label: 'Critical (9.0+)', value: '9' },
  { label: 'High (7.0+)', value: '7' },
  { label: 'Medium (4.0+)', value: '4' },
  { label: 'Low (0.1+)', value: '0.1' },
];

const parseMinCvss = (cvssFilter: string | undefined): number | undefined => {
  if (!cvssFilter?.trim()) return undefined;
  const scores = cvssFilter
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((score) => !Number.isNaN(score));
  if (!scores.length) return undefined;
  return Math.min(...scores);
};

const VulnerabilityDetection: FC = () => {
  const navigate = useNavigate();
  const { setDateRange, setSelectedEndpointId, selectedClientId } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.vulnerabilityDetection, {
      defaults: SECURITY_VULNERABILITY_FILTER_DEFAULTS,
    });
  const {
    severity = '',
    status = '',
    cvss = '',
    osPlatform = '',
    packageName = '',
  } = filters;
  const organizationId = selectedClientId ?? '';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortParams, setSortParams] = useState<VulnerabilitySortParams | null>(null);
  const [resetSortFlag, setResetSortFlag] = useState(false);
  const [packageSearch, setPackageSearch] = useState('');
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'vulnerability-detection');
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const canVulnerableEndpoints = canWidget('vulnerableEndpoints');
  const showUnavailable =
    permissionsResolved && !canSummary && !canList && !canVulnerableEndpoints;

  const vulnerabilityFilters = useMemo(
    () => ({
      severity: filters.severity,
      status: filters.status,
      minCvss: parseMinCvss(filters.cvss),
      osPlatform: filters.osPlatform,
      organizationId,
      packageName: filters.packageName,
    }),
    [filters.severity, filters.status, filters.cvss, filters.osPlatform, organizationId, filters.packageName]
  );

  const openVulnerabilityDetail = useCallback(
    (item: WazuhVulnerabilityItem) => {
      const id = getVulnerabilityId(item);
      if (!id) return;
      navigate(getSecurityVulnerabilityDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const {
    loading,
    summary,
    items: paged,
    endpoints: endpointsPaged,
    facets,
  } = useVulnerabilitiesData(
    page,
    rowsPerPage,
    search,
    vulnerabilityFilters,
    sortParams
  );

  const packageOptions = useMemo(() => {
    const query = packageSearch.trim().toLowerCase();
    return facets.packages
      .filter((name) => !query || name.toLowerCase().includes(query))
      .map((name) => ({ value: name, label: toFilterListLabel(name) }));
  }, [facets.packages, packageSearch]);

  const { exporting, handleExport } = useVulnerabilityExport(
    search,
    vulnerabilityFilters,
    sortParams
  );

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;
  const vulnerableEndpoints = endpointsPaged?.items ?? [];
  const vulnerableEndpointsTotal = endpointsPaged?.totalRecords ?? 0;

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'cvss',
        label: 'CVSS Score',
        icon: <SpeedIcon style={{ fontSize: 14 }} />,
        options: CVSS_OPTIONS,
        value: cvss,
        multiple: true,
        onChange: (value) => {
          setFilter('cvss', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: EDR_SEVERITY_FILTER_OPTIONS,
        value: severity,
        multiple: true,
        onChange: (value) => {
          setFilter('severity', value);
          setPage(0);
        },
      },
      {
        id: 'status',
        label: 'Status',
        options: STATUS_OPTIONS,
        value: status,
        multiple: true,
        onChange: (value) => {
          setFilter('status', value);
          setPage(0);
        },
      },
      {
        id: 'os',
        label: 'Operating System',
        icon: <ComputerIcon style={{ fontSize: 14 }} />,
        options: facets.operatingSystems.map((name) => ({
          label: toFilterListLabel(name),
          value: name,
        })),
        value: osPlatform,
        multiple: true,
        onChange: (value) => {
          setFilter('osPlatform', value);
          setPage(0);
        },
      },
      {
        id: 'package',
        label: 'Packages',
        variant: 'searchable' as const,
        value: packageName,
        multiple: true,
        searchableOptions: packageOptions,
        searchableOnSearch: setPackageSearch,
        onChange: (value: string) => {
          setFilter('packageName', value);
          setPackageSearch('');
          setPage(0);
        },
      },
    ],
    [
      severity,
      status,
      cvss,
      osPlatform,
      packageName,
      facets.operatingSystems,
      packageOptions,
      setFilter,
      setPackageSearch,
    ]
  );

  const handleResetFiltersBase = useSecurityEdrToolbarReset(
    resetFilters,
    setDateRange,
    setPage,
    setSelectedEndpointId
  );
  const handleResetFilters = () => {
    handleResetFiltersBase();
    setPackageSearch('');
    setSortParams(null);
    setResetSortFlag((prev) => !prev);
  };

  const handleSort = (columnId: string, direction: 'asc' | 'desc' | null) => {
    setSortParams(direction ? { column: columnId, sort: direction } : null);
    setPage(0);
  };

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'cveId',
        label: 'Event Name',
        sortable: true,
        width: 160,
        render: (row) => {
          const item = row as unknown as WazuhVulnerabilityItem;
          const title = item.title?.trim() || '—';
          const cveId = item.cveId?.trim() || '—';
          return (
            <Tooltip title={title !== '—' ? title : ''} placement="top" enterDelay={300}>
              <Box style={{ maxWidth: 160, minWidth: 0, overflow: 'hidden' }}>
                <Typography noWrap style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
                  {title}
                </Typography>
                <Typography noWrap style={{ ...edrMutedStyle, fontSize: 11 }}>
                  {cveId}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        id: 'cvssScore',
        label: 'CVSS',
        sortable: true,
        render: (row) => <CvssCell score={(row as unknown as WazuhVulnerabilityItem).cvssScore} />,
      },
      {
        id: 'severity',
        label: 'Severity',
        sortable: true,
        render: (row) => (
          <EdrSeverityPill
            level={vulnerabilitySeverityToLevel((row as unknown as WazuhVulnerabilityItem).severity)}
            label={toVulnerabilityTitle((row as unknown as WazuhVulnerabilityItem).severity)}
          />
        ),
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        render: (row) => (
          <EdrStatusPill
            tone={vulnerabilityStatusToTone((row as unknown as WazuhVulnerabilityItem).status)}
            label={toVulnerabilityTitle((row as unknown as WazuhVulnerabilityItem).status)}
          />
        ),
      },
      {
        id: 'osPlatform',
        label: 'Operating System',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhVulnerabilityItem).osPlatform}
          </span>
        ),
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        sortable: true,
        render: (row) => (
          <span style={edrMonoStyle}>{(row as unknown as WazuhVulnerabilityItem).endpoint}</span>
        ),
      },
      {
        id: 'hostName',
        label: 'Endpoint Label',
        sortable: false,
        render: (row) => (
          <span style={edrMutedStyle}>
            {formatEndpointLabel((row as unknown as WazuhVulnerabilityItem).hostName)}
          </span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhVulnerabilityItem).organization}
          </span>
        ),
      },
      {
        id: 'packageName',
        label: 'Package',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhVulnerabilityItem).packageName}
          </span>
        ),
      },
      {
        id: 'detectedAt',
        label: 'Detected',
        sortable: true,
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhVulnerabilityItem).detectedAt} />
        ),
      },
    ],
    []
  );

  const vulnerableEndpointColumns: Column[] = useMemo(
    () => [
      {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => {
          const item = row as unknown as WazuhVulnerableEndpointItem;
          const label = item.endpoint ?? item.name ?? '—';
          const canOpen = Boolean(item.systemWazuhAgentId?.trim());
          return (
            <span style={edrEndpointNameLinkStyle(canOpen)}>
              {label}
            </span>
          );
        },
      },
      {
        id: 'hostName',
        label: 'Endpoint Label',
        render: (row) => (
          <span style={edrMutedStyle}>
            {formatEndpointLabel((row as unknown as WazuhVulnerableEndpointItem).hostName)}
          </span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        render: (row) => {
          const item = row as unknown as WazuhVulnerableEndpointItem;
          return (
            <span style={edrMutedStyle}>
              {item.organization ?? item.organizationName ?? '—'}
            </span>
          );
        },
      },
      {
        id: 'osPlatform',
        label: 'Operating System',
        render: (row) => {
          const item = row as unknown as WazuhVulnerableEndpointItem;
          return (
            <span style={edrMutedStyle}>
              {toFilterListLabel(item.operatingSystem ?? item.osPlatform) || '—'}
            </span>
          );
        },
      },
      {
        id: 'totalVulnerabilities',
        label: 'Total Vulnerabilities',
        align: 'right',
        render: (row) => {
          const item = row as unknown as WazuhVulnerableEndpointItem;
          return (
            <span style={{ color: EDR_COLORS.textSecondary }}>
              {formatEdrNumber(item.totalVulnerabilities ?? item.vulnerabilityCount)}
            </span>
          );
        },
      },
      {
        id: 'open',
        label: 'Open',
        align: 'right',
        render: (row) => {
          const open = (row as unknown as WazuhVulnerableEndpointItem).open ?? 0;
          return (
            <span
              style={{
                fontWeight: 600,
                color: open > 0 ? EDR_COLORS.critical : EDR_COLORS.success,
              }}
            >
              {formatEdrNumber(open)}
            </span>
          );
        },
      },
      {
        id: 'highestSeverity',
        label: 'Highest Severity',
        render: (row) => {
          const item = row as unknown as WazuhVulnerableEndpointItem;
          const highestSeverity = item.highestSeverity ?? item.severity;
          return (
            <EdrSeverityPill
              level={vulnerabilitySeverityToLevel(highestSeverity)}
              label={toVulnerabilityTitle(highestSeverity) || '—'}
            />
          );
        },
      },
      {
        id: 'topCvss',
        label: 'Top CVSS',
        render: (row) => {
          const item = row as unknown as WazuhVulnerableEndpointItem;
          return <CvssCell score={item.topCvss ?? item.maxCvss ?? 0} />;
        },
      },
    ],
    []
  );

  const vulnerableEndpointsToolbar = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: EDR_COLORS.textPrimary,
        }}
      >
        Vulnerable Endpoints
      </Typography>
      <Typography style={{ fontSize: 12, color: EDR_COLORS.textMuted }}>
        {formatEdrNumber(vulnerableEndpointsTotal)} endpoints affected
      </Typography>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Vulnerabilities')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Vulnerabilities"
          subtitle="Identify, prioritize, and remediate security vulnerabilities across protected endpoints."
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <EdrGlobalScopeFilters
            pageOrganizationId={organizationId || undefined}
            onChange={() => setPage(0)}
          />
          {canList ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          ) : null}
        </Box>

        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.vulnerabilityDetection" />
        ) : null}

        {canSummary ? (
        <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <EdrKpiCard
            label="Total Vulnerabilities"
            value={(
              <>
                {formatEdrNumber(summary?.activeVulnerabilities)}
                <EdrStatusPill tone="high" label="Open" dot={false} />
              </>
            )}
            icon={<BugReportIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.high}
            accent={EDR_COLORS.high}
          >
            <EdrStatusPill tone="neutral" label={`${formatEdrNumber(summary?.totalVulnerabilities)} Total`} />
            <EdrStatusPill tone="success" label={`${formatEdrNumber(summary?.resolvedVulnerabilities)} Resolved`} />
          </EdrKpiCard>
          <EdrKpiCard
            label="Critical Vulnerabilities"
            value={(
              <>
                {formatEdrNumber(summary?.activeCriticalVulnerabilities)}
                <EdrStatusPill tone="critical" label="Open" dot={false} />
              </>
            )}
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
            accent={EDR_COLORS.critical}
          >
            <EdrStatusPill tone="neutral" label={`${formatEdrNumber(summary?.criticalVulnerabilities)} Total`} />
            <EdrStatusPill
              tone="success"
              label={`${formatEdrNumber(summary?.resolvedCriticalVulnerabilities)} Resolved`}
            />
          </EdrKpiCard>
          <EdrKpiCard
            label="High Risk Endpoints"
            value={formatEdrNumber(summary?.highRiskEndpoints)}
            icon={<ComputerIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.high}
            accent={EDR_COLORS.high}
          >
            Endpoints most exposed to risk
          </EdrKpiCard>
          <EdrKpiCard
            label="Remediation"
            value={`${formatEdrNumber(summary?.remediationPercent)}%`}
            icon={<TrendingUpIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.success}
            accent={EDR_COLORS.success}
          >
            Progress towards closing vulnerabilities
          </EdrKpiCard>
        </Box>
        ) : null}

        {canVulnerableEndpoints ? (
        <EdrDataCard toolbar={vulnerableEndpointsToolbar}>
          <Table
            columns={vulnerableEndpointColumns}
            data={vulnerableEndpoints as unknown as TableData[]}
            loading={loading}
            showPagination={false}
            onRowClick={(row) => {
              const item = row as unknown as WazuhVulnerableEndpointItem;
              const systemWazuhAgentId = item.systemWazuhAgentId?.trim();
              if (!systemWazuhAgentId) return;
              navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
                state: { endpointName: item.endpoint ?? item.name },
              });
            }}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}

        {canList ? (
        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Vulnerabilities…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onSort={handleSort}
            resetSortFlag={resetSortFlag}
            onRowClick={(row) => openVulnerabilityDetail(row as unknown as WazuhVulnerabilityItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}
      </Box>
    </>
  );
};

export default VulnerabilityDetection;
